import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ICommonVideoPlayer } from '@epicuro-next/ataraxia/models';
import Player from '@vimeo/player';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

const SAFE_BUFFERING_TIME_DELAY = 350;

@Component({
  selector: 'atx-vimeo-player',
  templateUrl: './vimeo-player.component.html',
  styleUrls: ['./vimeo-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class VimeoPlayerComponent
  implements AfterViewInit, OnChanges, ICommonVideoPlayer, OnDestroy
{
  public pipActive = false;

  @ViewChild('vimeoPlayerTpl')
  public vimeoTmp: ElementRef;

  @Input()
  public url: string;

  @Input()
  public autoplay = false;

  @Input()
  public muted = true;

  @Input()
  public controls = true;

  private playCommand$$ = new BehaviorSubject(this.autoplay);

  @Input()
  public set play(play: boolean) {
    this.playCommand$$.next(play ?? this.autoplay);
  }

  @Output()
  public videoReady = new EventEmitter();

  @Output()
  public videoError = new EventEmitter();

  public vimeoPlayer: Player | null = null;

  private destroy$$ = new Subject<void>();

  constructor(private renderer: Renderer2, private cdr: ChangeDetectorRef) {}

  public ngAfterViewInit() {
    this.startPlayer();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.url && !changes.url.firstChange) {
      this.startPlayer();
    }
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.complete();

    this.vimeoPlayer?.destroy();
  }

  public requestPictureInPicture() {
    this.pipActive = true;
    this.cdr.markForCheck();
  }

  public exitPictureInPicture() {
    this.pipActive = false;
    this.cdr.markForCheck();
  }

  private playerReady() {
    this.videoReady.emit();

    if (this.muted) {
      this.vimeoPlayer?.setMuted(true);
    }

    this.playCommand$$
      .pipe(debounceTime(SAFE_BUFFERING_TIME_DELAY), takeUntil(this.destroy$$))
      .subscribe((value) => this.videoController(value));
  }

  private startPlayer() {
    if (this.vimeoPlayer) {
      this.vimeoPlayer.destroy();
    }

    this.vimeoPlayer = new Player(this.vimeoTmp.nativeElement, {
      url: this.url,
      controls: this.controls,
      autoplay: this.autoplay,
      muted: this.muted,
    });

    this.vimeoPlayer.ready().then(() => {
      this.playerReady();
    });

    if (!this.muted) {
      this.vimeoPlayer.setVolume(1);
    }
    this.vimeoPlayer.on('loaded', () => {
      const playerFirstChild = this.vimeoTmp.nativeElement.children[0];
      // fix for https://epicuro.atlassian.net/browse/FE-2424
      // vimeo adds before iframe elements that break html, so we need to reset it
      if (playerFirstChild instanceof HTMLDivElement) {
        this.renderer.setStyle(playerFirstChild, 'position', 'unset');
      }
      this.videoReady.emit();
    });

    this.vimeoPlayer.on('error', () => {
      this.videoError.emit();
    });
  }

  private videoController(play: boolean) {
    if (!this.vimeoPlayer) {
      return;
    }

    if (play) {
      this.vimeoPlayer.play();
      return;
    }

    this.vimeoPlayer.pause();
  }
}
